import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const ACCENT = '#3b82f6';
const TOTAL_Q = 10;

const TEAM_COLORS = {
  'Red Bull Racing': '#3671C6', 'Red Bull': '#3671C6',
  'Ferrari': '#E8002D',
  'Mercedes': '#27F4D2',
  'McLaren': '#FF8000',
  'Aston Martin': '#229971',
  'Alpine': '#0093CC', 'Alpine F1 Team': '#0093CC',
  'Williams': '#64C4FF',
  'AlphaTauri': '#5E8FAA', 'Scuderia AlphaTauri': '#5E8FAA', 'RB': '#5E8FAA', 'Visa Cash App RB Formula One Team': '#5E8FAA',
  'Alfa Romeo': '#C92D4B', 'Alfa Romeo Racing': '#C92D4B', 'Kick Sauber': '#52E252',
  'Haas F1 Team': '#B6BABD', 'Haas': '#B6BABD',
};

const teamColor = (team) => TEAM_COLORS[team] || '#6b7280';

const DriverCard = ({ driver, state, onClick }) => {
  const bg =
    state === 'correct' ? 'bg-green-500/20 border-green-500'
    : state === 'wrong'   ? 'bg-red-500/20 border-red-500'
    : state === 'reveal'  ? 'bg-green-500/10 border-green-500/60'
    : 'bg-zinc-900 border-zinc-700 hover:border-blue-500/60 hover:bg-blue-500/5 cursor-pointer';

  return (
    <button
      onClick={onClick}
      disabled={state !== 'idle'}
      className={`w-full rounded-xl border p-3 flex items-center gap-3 transition-all ${bg}`}
    >
      <div className="w-1 h-10 rounded-full shrink-0" style={{ backgroundColor: teamColor(driver.team) }} />
      <div className="text-left min-w-0 flex-1">
        <p className="text-white font-black text-sm leading-tight truncate">{driver.name}</p>
        <p className="text-zinc-500 text-[10px] truncate">{driver.team}</p>
      </div>
      {state === 'correct' && <span className="text-green-400 font-black text-lg shrink-0">✓</span>}
      {state === 'wrong'   && <span className="text-red-400 font-black text-lg shrink-0">✗</span>}
      {state === 'reveal'  && <span className="text-green-400 font-black text-lg shrink-0">✓</span>}
    </button>
  );
};

const QuiMenait = () => {
  const { user, refreshUser } = useAuth();
  const [phase, setPhase]       = useState('loading');
  const [questions, setQuestions] = useState([]);
  const [raceKey, setRaceKey]   = useState(null);
  const [raceName, setRaceName] = useState('');
  const [qIndex, setQIndex]     = useState(0);
  const [chosen, setChosen]     = useState(null);
  const [answered, setAnswered] = useState(false);
  const [localScore, setLocalScore] = useState(0);
  const [answers, setAnswers]   = useState([]);
  const [result, setResult]     = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sessionXp, setSessionXp]   = useState(0);
  const [sessionCount, setSessionCount] = useState(0);
  const seenRaces = useRef([]);
  const timerRef  = useRef(null);
  const hasInitialized = useRef(false);

  const loadSession = useCallback(async () => {
    if (!user) { setPhase('error'); return; }
    setPhase('loading');
    try {
      const exclude = seenRaces.current.join(',');
      const r = await api.get(`/game/lead/session${exclude ? `?excludeRaces=${exclude}` : ''}`);
      seenRaces.current = [...seenRaces.current, r.data.raceKey];
      setQuestions(r.data.questions);
      setRaceKey(r.data.raceKey);
      setRaceName(r.data.raceName);
      setQIndex(0);
      setChosen(null);
      setAnswered(false);
      setLocalScore(0);
      setAnswers([]);
      setResult(null);
      setPhase('playing');
    } catch { setPhase('error'); }
  }, [user]);

  useEffect(() => {
    if (user && !hasInitialized.current) {
      hasInitialized.current = true;
      loadSession();
    }
  }, [user, loadSession]);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const current = questions[qIndex];

  const handleAnswer = (driverId) => {
    if (answered || !current) return;
    setChosen(driverId);
    setAnswered(true);

    const correct = driverId === current.correctId;
    if (correct) setLocalScore(p => p + 100);

    const newAnswers = [...answers, { lapNumber: current.lapNumber, driverId }];
    setAnswers(newAnswers);

    timerRef.current = setTimeout(async () => {
      const nextIdx = qIndex + 1;
      if (nextIdx < questions.length) {
        setQIndex(nextIdx);
        setChosen(null);
        setAnswered(false);
      } else {
        setPhase('submitting');
        setSubmitting(true);
        try {
          const r = await api.post('/game/lead/submit', { raceKey, answers: newAnswers });
          setSessionXp(p => p + r.data.xpEarned);
          setSessionCount(p => p + 1);
          setResult(r.data);
          setPhase('done');
          if (r.data.xpEarned > 0) refreshUser();
        } catch { setPhase('error'); }
        finally { setSubmitting(false); }
      }
    }, 900);
  };

  const getCardState = (driverId) => {
    if (!answered) return 'idle';
    if (driverId === chosen && driverId === current?.correctId) return 'correct';
    if (driverId === chosen && driverId !== current?.correctId) return 'wrong';
    if (driverId !== chosen && driverId === current?.correctId) return 'reveal';
    return 'idle';
  };

  // ── LOADING ────────────────────────────────────────────────────────────────
  if (phase === 'loading' || phase === 'submitting') return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3">
      <div className="w-8 h-8 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
      <p className="text-zinc-400 text-sm">
        {phase === 'submitting' ? 'Calcul du score…' : 'Chargement des données de course…'}
      </p>
      {phase === 'loading' && <p className="text-zinc-600 text-xs">Première visite sur ce GP = quelques secondes</p>}
    </div>
  );

  if (phase === 'error') return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-zinc-400">Erreur lors du chargement.</p>
      <button onClick={loadSession} className="btn-secondary">Réessayer</button>
      <Link to="/box-box" className="btn-secondary">← Box Box Hub</Link>
    </div>
  );

  // ── RESULTS ────────────────────────────────────────────────────────────────
  if (phase === 'done' && result) {
    const correctCount = result.details.filter(d => d.correct).length;
    return (
      <div className="max-w-lg mx-auto px-4 py-8">
        <div className="text-center mb-6">
          <p className="text-6xl mb-3">
            {correctCount === 10 ? '🏆' : correctCount >= 7 ? '🔥' : correctCount >= 4 ? '👍' : '💀'}
          </p>
          <h1 className="text-3xl font-black text-white mb-1">{correctCount}/10 correctes</h1>
          <p className="text-zinc-400 text-sm mb-3">{result.raceName}</p>
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl mb-2"
            style={{ background: ACCENT + '18', border: `1px solid ${ACCENT}44` }}>
            <span className="font-black text-xl" style={{ color: ACCENT }}>+{result.xpEarned} XP</span>
          </div>
          {sessionCount > 1 && (
            <p className="text-zinc-500 text-xs mt-1">Session : <span className="text-white font-bold">+{sessionXp} XP</span> en {sessionCount} courses</p>
          )}
          {result.levelInfo?.leveledUp && (
            <p className="text-yellow-400 font-black mt-2">🎉 Niveau {result.levelInfo.level} atteint !</p>
          )}
        </div>

        <div className="rounded-2xl border border-zinc-800 overflow-hidden mb-5">
          <div className="px-4 py-2.5 border-b border-zinc-800">
            <span className="text-white font-black text-xs uppercase tracking-wider">Détail des questions</span>
          </div>
          <div className="divide-y divide-zinc-800/50">
            {result.details.map((d, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3">
                <div className="min-w-0">
                  <p className="text-zinc-400 text-xs">Tour {d.lapNumber}</p>
                  <p className={`text-sm font-bold ${d.correct ? 'text-green-400' : 'text-red-400'}`}>
                    {d.correct ? `✓ ${d.correctName}` : `✗ ${d.correctName}`}
                  </p>
                </div>
                <span className={`text-xs font-black px-2 py-0.5 rounded-full ${d.correct ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                  {d.correct ? '+100' : '+0'}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <button onClick={loadSession} className="w-full py-3 rounded-xl font-black text-white text-sm"
            style={{ background: ACCENT }}>
            🏎️ Nouvelle course
          </button>
          <Link to="/box-box" className="btn-secondary text-center">← Box Box Hub</Link>
        </div>
      </div>
    );
  }

  // ── PLAYING ────────────────────────────────────────────────────────────────
  if (!current) return null;

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-5">
        <p className="text-[10px] font-black uppercase tracking-widest mb-0.5" style={{ color: ACCENT }}>Qui Menait ?</p>
        <h1 className="text-2xl font-black text-white leading-tight">Au tour X, qui était en tête ?</h1>
        <p className="text-zinc-500 text-xs mt-0.5">
          {raceName}
          {sessionCount > 0 && <span> · Session : <span className="text-white">+{sessionXp} XP</span></span>}
        </p>
      </div>

      {/* Progress */}
      <div className="flex gap-1 mb-5">
        {Array.from({ length: questions.length }).map((_, i) => (
          <div key={i} className="h-1.5 rounded-full flex-1 transition-all"
            style={{
              backgroundColor:
                i < qIndex ? ACCENT
                : i === qIndex ? ACCENT + '55'
                : '#27272a'
            }} />
        ))}
      </div>

      {/* Lap info card */}
      <div className="rounded-2xl border border-blue-500/40 bg-blue-500/5 p-5 mb-5 text-center">
        <p className="text-zinc-400 text-xs uppercase tracking-wider mb-1">Question {qIndex + 1}/{questions.length}</p>
        <p className="text-white font-black text-4xl mb-1">Tour {current.lapNumber}</p>
        <p className="text-zinc-500 text-sm">sur {current.totalLaps}</p>
        <p className="text-zinc-400 text-xs mt-3">Qui était en <span className="text-white font-bold">P1</span> à ce moment de la course ?</p>
      </div>

      {/* Choices */}
      <div className="flex flex-col gap-2">
        {current.choices.map(driver => (
          <DriverCard
            key={driver.driverId}
            driver={driver}
            state={getCardState(driver.driverId)}
            onClick={() => handleAnswer(driver.driverId)}
          />
        ))}
      </div>

      <div className="mt-4 text-center">
        <p className="text-zinc-700 text-[10px]">
          Score actuel : <span className="text-zinc-500">{localScore} pts</span>
          &nbsp;·&nbsp;Max : {questions.length * 100} pts
        </p>
      </div>
    </div>
  );
};

export default QuiMenait;
